import { Close, Share } from '@mui/icons-material';
import { Badge, Box, Skeleton, Tab, Tabs, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AnalyticsEvent, useAnalytics } from '../../components/Providers/AnalyticsProvider';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import { findPrompt, updatePrompt } from '../../redux/actions/promptActions';
import { myFolder } from '../../redux/consts';
import { AppDispatch } from '../../redux/store';
import { StyledIconButton } from '../../theme/CustomComponents';
import { Commentable, PromptBuilderMode, PromptDrawerMode, PromptView } from '../../Types/enums';
import { Prompt } from '../../Types/prompt';
import { getHost } from '../../util/getHost';
import AutoTruncatingTypographyWithTooltip from '../AutoTruncatingTypographyWithTooltip';
import Comments from '../Comments/Comments';
import { useAuth } from '../Providers/AuthProvider';
import { useDrawer } from '../Providers/DrawerProvider';
import ShareButton from '../ShareButton';
import PromptBuilder from './PromptBuilder';
import PromptForm from './PromptForm';
import PromptVersionSelect from './PromptVersionSelect';
import PromptStepBuilder from './StepBuilder/PromptStepBuilder';

enum PromptDrawerTabs {
  Prompt = 0,
  Comments = 1,
}

export interface PromptDrawerProps {
  mode?: PromptDrawerMode;
  promptId: string | undefined;
  promptView: PromptView | undefined;
  isAIAppStore: boolean;
  onNewPrompt?: (prompt: Prompt) => void;
  onEditPrompt?: (prompt: Prompt) => void;
}

const PromptDrawer: React.FC<PromptDrawerProps> = ({
  mode,
  promptId,
  promptView,
  isAIAppStore,
  onNewPrompt,
  onEditPrompt,
}) => {
  const [isExternalPrompt, setIsExternalPrompt] = useState(isAIAppStore);
  const [originalPrompt, setOriginalPrompt] = useState<Prompt>();
  const [tab, setTab] = useState<PromptDrawerTabs>(PromptDrawerTabs.Prompt);
  const dispatch = useDispatch<AppDispatch>();
  const { closeDrawer } = useDrawer();
  const { currentUser, isAdmin } = useAuth();
  const { copyToClipboard: shareCopyToClipboard } = useCopyToClipboard();
  const analytics = useAnalytics();
  const [drawerMode, setdrawerMode] = useState<PromptDrawerMode>(
    mode ?? (promptId ? PromptDrawerMode.Edit : PromptDrawerMode.Create)
  );
  const [stepBuilderPromptText, setStepBuilderPromptText] = useState<string>('');

  const handleStepBuilderSubmit = useCallback((text: string) => {
    setStepBuilderPromptText(text);
    setdrawerMode(PromptDrawerMode.Create);
  }, []);

  const handleStepBuilderBack = useCallback(() => {
    setdrawerMode(PromptDrawerMode.Builder);
  }, []);

  const handleStepBuilderClick = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_PROMPT_STEP_BUILDER_CLICK);
    setdrawerMode(PromptDrawerMode.StepByStep);
  }, [analytics]);

  const handleQuickCreateClick = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_PROMPT_QUICK_CREATE_CLICK);
    setdrawerMode(PromptDrawerMode.Create);
  }, [analytics]);

  const handleCreateExternalPromptClick = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_CREATE_EXTERNAL_PROMPT_CLICK);
    setIsExternalPrompt(true);
    setdrawerMode(PromptDrawerMode.Create);
  }, [analytics]);

  const handleCopyLink = useCallback(async () => {
    if (!promptId) {
      return;
    }

    analytics.event(
      AnalyticsEvent.WM_NIGEL_PROMPT_SHARE,
      {
        id: promptId,
      },
      {
        promptId: promptId,
      }
    );

    shareCopyToClipboard(getHost() + `/folder/${myFolder.id}/prompts?promptId=${promptId}`);
  }, [promptId, shareCopyToClipboard, analytics]);

  const isUserAllowedToSharePrompt = useMemo(() => {
    if (!originalPrompt || !currentUser) return false;

    const isOwner = originalPrompt.ownerId === currentUser.id;
    const isSharedWithUser = Object.keys(originalPrompt.sharedWith ?? {}).includes(currentUser.id);

    return isAdmin || isOwner || isSharedWithUser;
  }, [originalPrompt, currentUser, isAdmin]);

  const onShare = useCallback(
    async (sharedWith: string[]) => {
      if (!promptId) return;

      const updatedPrompt = await dispatch(
        updatePrompt({ promptId, prompt: { share: sharedWith } })
      ).unwrap();

      setOriginalPrompt(updatedPrompt);
    },
    [promptId, dispatch]
  );

  useEffect(() => {
    if (promptId) {
      const fetchPrompt = async () => {
        const newPrompt = await dispatch(findPrompt(promptId)).unwrap();
        setOriginalPrompt(newPrompt);
      };

      fetchPrompt();
    }
  }, [promptId, dispatch]);

  if (drawerMode === PromptDrawerMode.Builder) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            height: '95px',
            display: 'flex',
            alignItems: 'center',
            padding: 2,
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <AutoTruncatingTypographyWithTooltip text="Prompt Builder" variant="h5" />
          <StyledIconButton onClick={closeDrawer}>
            <Close />
          </StyledIconButton>
        </Box>

        <PromptBuilder
          mode={isAIAppStore ? PromptBuilderMode.EXTERNAL_ONLY : PromptBuilderMode.INTERNAL_ONLY}
          onQuickCreate={handleQuickCreateClick}
          onStepByStepCreate={handleStepBuilderClick}
          onCreateExternalPrompt={handleCreateExternalPromptClick}
        />
      </Box>
    );
  } else if (drawerMode === PromptDrawerMode.StepByStep) {
    return (
      <PromptStepBuilder onComplete={handleStepBuilderSubmit} onClose={handleStepBuilderBack} />
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: '95px',
          display: 'flex',
          alignItems: 'center',
          padding: 2,
          justifyContent: 'space-between',
          borderBottom: '1px solid #ddd',
        }}
      >
        {promptId && !originalPrompt ? (
          <>
            <Skeleton variant="text" width="80%" height={40} />
            <Skeleton variant="circular" width={40} height={40} />
          </>
        ) : (
          <>
            <AutoTruncatingTypographyWithTooltip
              text={
                promptId && originalPrompt?.title
                  ? originalPrompt?.title
                  : isAIAppStore
                  ? 'New App'
                  : 'New Prompt'
              }
              variant="h5"
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              {promptId && (
                <Tooltip title={`Copy link to this ${isAIAppStore ? 'app' : 'prompt'}`}>
                  <StyledIconButton onClick={handleCopyLink}>
                    <Badge
                      color="primary"
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                      overlap="circular"
                    >
                      <Share />
                    </Badge>
                  </StyledIconButton>
                </Tooltip>
              )}
              <StyledIconButton onClick={closeDrawer}>
                <Close />
              </StyledIconButton>
            </Box>
          </>
        )}
      </Box>
      {promptId && !originalPrompt ? (
        <Box
          sx={{
            display: 'flex',
            px: 1,
            pt: 1,
            gap: 2,
          }}
        >
          <Skeleton variant="rectangular" width={100} height={40} />
          <Skeleton variant="rectangular" width={120} height={40} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 1,
            pt: 1,
          }}
        >
          <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
            <Tab label={isAIAppStore ? 'App' : 'Prompt'} value={PromptDrawerTabs.Prompt} />
            <Tab label="Comments" value={PromptDrawerTabs.Comments} disabled={!promptId} />
          </Tabs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {isUserAllowedToSharePrompt && originalPrompt && (
              <ShareButton
                sharedWith={originalPrompt.sharedWith}
                onSave={onShare}
                ignoreIds={[originalPrompt.ownerId as string]}
              />
            )}
            {promptId && <PromptVersionSelect promptId={promptId} />}
          </Box>
        </Box>
      )}
      {tab === PromptDrawerTabs.Prompt && (
        <PromptForm
          promptId={promptId}
          originalPrompt={originalPrompt}
          isAIAppStore={isAIAppStore}
          external={isExternalPrompt}
          promptView={promptView}
          onNewPrompt={onNewPrompt}
          onEditPrompt={onEditPrompt}
          stepBuilderPromptText={stepBuilderPromptText}
        />
      )}

      {tab === PromptDrawerTabs.Comments && promptId && (
        <Box
          sx={{
            height: 'calc(100% - 175px)', // 125px for the header + 50px for the tabs
          }}
        >
          <Comments entityId={promptId} entityType={Commentable.Prompt} />
        </Box>
      )}
    </Box>
  );
};

export default PromptDrawer;
